/**
 * /moniteur/bilans — Bilans à rédiger + historique des bilans faits.
 */
import React, { useEffect, useState } from 'react';
import { ClipboardCheck, ClipboardList, Clock } from 'lucide-react';
import api from '../../api';

export default function MoniteurBilansPage() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Bilans · Espace enseignant';
    (async () => {
      try {
        // Prendre les 30 derniers jours
        const to = new Date().toISOString().slice(0, 10);
        const from = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
        const r = await api.boListLessons({ dateFrom: from, dateTo: to });
        setLessons(r?.items || []);
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const aRediger = lessons.filter(l => l.status === 'effectue' && !l.bilan);
  const faits = lessons.filter(l => l.status === 'effectue' && l.bilan);

  return (
    <div className="max-w-5xl" data-testid="moniteur-bilans-page">
      <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Comptes-rendus</div>
      <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Bilans</h1>
      <p className="text-sm text-white/60 mt-2 mb-6">Chaque leçon effectuée doit être suivie d’un bilan pour l’élève.</p>

      {loading && <div className="text-white/40 text-sm">Chargement…</div>}

      {!loading && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.05] p-4">
              <div className="w-9 h-9 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center mb-2"><ClipboardList size={15} /></div>
              <div className="text-3xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{aRediger.length}</div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold mt-1">À rédiger</div>
            </div>
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.05] p-4">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-2"><ClipboardCheck size={15} /></div>
              <div className="text-3xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{faits.length}</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold mt-1">Faits (30 j)</div>
            </div>
          </div>

          {aRediger.length > 0 && (
            <div className="mb-8">
              <h2 className="text-white font-black text-lg mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>À rédiger</h2>
              <div className="space-y-2">
                {aRediger.map(l => (
                  <div key={l.id} className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4 flex items-center gap-3" data-testid={`bilan-todo-${l.id}`}>
                    <div className="w-1 h-10 rounded-full bg-amber-500" />
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm">{l.student_name || 'Élève'}</div>
                      <div className="text-white/50 text-xs mt-0.5 flex items-center gap-2">
                        <Clock size={11} /> {new Date(l.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} · {(l.start_time || '').slice(0, 5)} · {l.duration_min} min
                      </div>
                    </div>
                    <button className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-black transition" data-testid={`btn-rediger-bilan-${l.id}`}>
                      Rédiger
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {aRediger.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center" data-testid="no-bilans-todo">
              <ClipboardCheck className="w-8 h-8 text-white/30 mx-auto mb-2" />
              <div className="text-white/60 text-sm">Aucun bilan en attente. Bien joué 👍</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
